import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import type { IIntegrationUIOption, IIntegrationUISelectSettingField } from '@feathr/components';
import CrmOptionSelect from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/CrmOptionSelect';

import { useFundOptions } from './RaisersEdgeFundSelect.hooks';

interface IProps {
  integration: BlackbaudRaisersEdgeIntegration;
  /** Loads the funds the first time this is true, i.e. once donation upsync is on. */
  enabled: boolean;
  /** The fund field from the config: its label and placeholder. */
  field: IIntegrationUISelectSettingField;
  /** Disables the select with this reason, e.g. while the page saves. */
  disabledReason?: string;
}

function RaisersEdgeFundSelect({
  disabledReason,
  enabled,
  field,
  integration,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const options = useFundOptions(integration, enabled);

  // Stage the edit on the model; the page's Save & Sync persists it.
  function handleSelect(option: IIntegrationUIOption): void {
    integration.set({ fundraising: { ...integration.get('fundraising'), fund_id: option.id } });
  }

  return (
    <CrmOptionSelect
      disabledReason={disabledReason}
      field={field}
      name={'raisers-edge-fund'}
      noOptionsMessage={t('No funds found')}
      onSelect={handleSelect}
      options={options}
      value={integration.get('fundraising')?.fund_id}
    />
  );
}

export default observer(RaisersEdgeFundSelect);
